"use client";

import React, { useMemo } from "react";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { cn } from "utils";
import { useParams } from "next/navigation";
import { ShellLink } from "@/src/components";
import { FormattedPrice } from "../../PriceDisplay/FormattedPrice";
import Translations from "@translate/components/AlphaPick/EdPicksForYou/EdPicksForYouItem";
import { I18nUtil } from "@utils/i18n";
import { NextImage } from "@/src/components/NextImage";
import { ScoreRingBadge } from "@/src/components/ScoreRingBadge";
import { RecommendScopeFormattedAsset } from "@/src/hooks/useRecommendScopeContentsQuery";
import { getCurrencySymbol } from "@/src/utils/stock";
import { formatBigNumber } from "@/src/utils";
const I18n = I18nUtil.createScoped(Translations);

export interface EdPicksForYouItemProps extends RecommendScopeFormattedAsset {}

const resolveTrend = (
  item: Pick<RecommendScopeFormattedAsset, "change1d">,
): "up" | "down" | "neutral" => {
  if (!item.change1d) {
    return "neutral";
  }
  const t = String(item.change1d).trim();
  if (t.startsWith("-")) {
    return "down";
  }
  if (t.startsWith("+") || /^\d/.test(t)) {
    return "up";
  }
  return "neutral";
};

// 根据market条件展示sysbol或short_name
const resolveSymbol = (
  item: Pick<
    RecommendScopeFormattedAsset,
    "symbol" | "short_name" | "market" | "company_name"
  >,
) => {
  if (item.market === "HK") {
    return item.company_name || item.symbol;
  }
  return item.symbol;
};

export const EdPicksForYouItem: React.FC<RecommendScopeFormattedAsset> = ({
  logo,
  symbol,
  price,
  change1d,
  multi_language_contents,
  overall_score,
  short_name,
  currency,
  href,
  market,
  id,
  company_name,
  onClick,
}) => {
  const params = useParams<{ locale: string }>();
  const locale = (params?.locale as string) || "en";
  const language = I18nUtil.getBackgroundLanguageKey(locale);
  const trend = useMemo(() => resolveTrend({ change1d }), [change1d]);
  return (
    <ShellLink
      data-openreplay-label={`EdPicksForYouItem · Link | ${href || "#"}`}
      href={href || "#"}
      className="flex w-full items-center justify-between gap-2"
      data-tracking-eventname="alpha_for_you_pick_tap"
      data-tracking-event-key-score={overall_score}
      data-tracking-event-key-assetsid={id}
      data-tracking-event-key-market={market}
      onClick={onClick}
    >
      {/* 设计稿 StockCard/Body：与评分列垂直居中对齐；内部 4px 行距 */}
      <div className="flex min-h-px min-w-0 flex-1 flex-col gap-1 items-start">
        {/* StockCard/Content：头像与信息行 8px */}
        <div className="flex w-full min-w-0 items-center gap-2">
          <div className="relative size-6 shrink-0 overflow-hidden rounded-full group-loading:skeleton group-loading:rounded-full">
            {logo && (
              <NextImage
                src={logo}
                alt={symbol}
                width={24}
                height={24}
                className="size-full rounded-full object-cover"
              />
            )}
          </div>
          {/* StockCard/InfoRow：名称与价格行 4px */}
          <div className="flex min-w-0 items-center gap-1 group-loading:min-w-40 group-loading:min-h-6 group-loading:skeleton group-loading:rounded-1">
            <span className="body_bold_l shrink-0 whitespace-nowrap text-ellipsis max-w-[140px] overflow-hidden text-content_primary">
              {resolveSymbol({ short_name, company_name, symbol, market })}
            </span>
            {/* StockCard/PriceRow：价格与涨跌 2px */}
            {price && (
              <div className="flex min-w-0 items-center gap-0.5">
                <FormattedPrice
                  price={price.toString()}
                  currencySymbol={getCurrencySymbol(currency)}
                  className={{
                    container:
                      "body_medium_s shrink-0 tracking-[0.1px] text-content_tertiary",
                  }}
                />
                <div className="flex shrink-0 items-center">
                  <OspIcons
                    name={
                      trend === "up"
                        ? "upvote"
                        : trend === "down"
                          ? "downvote"
                          : "minus"
                    }
                    size={10}
                    className={cn({
                      "text-content_accent1_primary": trend === "up",
                      "text-content_accent2_primary": trend === "down",
                      "text-content_secondary": trend === "neutral",
                    })}
                  />
                  <span
                    className={cn("body_medium_s tracking-[0.1px]", {
                      "text-content_accent1_primary": trend === "up",
                      "text-content_accent2_primary": trend === "down",
                      "text-content_secondary": trend === "neutral",
                    })}
                  >
                    {formatBigNumber(Math.abs(change1d), {
                      precision: 2,
                    })}
                    %
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        <p className="body_m w-full min-w-0 text-content_secondary min-h-5 group-loading:skeleton group-loading:rounded-1">
          {multi_language_contents?.[language]?.title}
        </p>
      </div>
      {/* StockCard/BottomRow：与设计稿一致贴右、列内居中 */}
      <ScoreRingBadge
        value={overall_score}
        label={I18n.t("Score")}
        className="whitespace-nowrap group-loading:size-8.5 group-loading:skeleton group-loading:rounded-full"
      />
    </ShellLink>
  );
};
